import { TypographyHeading } from "shared/molecules/typography-layout";
import {
    Box,
    InputLabel,
    Paper,
    FormControl,
    Select,
    MenuItem,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    IconButton,
    Typography,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "pages/build-test/loader";
import { useEffect, useState } from "react";
import {
    GET_LIVE_SESSION_INSTANCES,
    GET_LIVE_SESSION_INSTANCE_RECORDED_VIDEO,
} from "utils/graphql/services/live-classes/queries";
import { useLazyQuery } from "@apollo/client";
import NoDataFound from "shared/molecules/no-data";
import { Stack } from "@mui/material"
import { table } from "./helper"
import liveClassClientService from "utils/graphql/liveclassClient";
import { useHistory, useLocation } from "react-router-dom";
import { utcToIst } from "utils/time-util/utcIstTime";
import CollapsibleTable from "shared/organisms/tables/collapsible-table";

export default function SessionInstances() {
    const history = useHistory();
    const location: any = useLocation();
    const liveSessionId = location?.state?.row?.id || location?.state?.row?.liveSessionId || null;
    const externalLiveSessionsInstanceId =
        location?.state?.externalLiveSessionsInstanceId || "";
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [recordingDialog, setRecordingDialog] = useState<{
        open: boolean;
        row: any;
        recordedUrl: string;
        errorMessage: string;
    }>({ open: false, row: null, recordedUrl: "", errorMessage: "" });

    const [getSessionInstancesList, { loading, error, data }] = useLazyQuery(
        GET_LIVE_SESSION_INSTANCES,
        {
            client: liveClassClientService,
        },
    );

    const [getRecordedVideo, { loading: recordingLoading }] = useLazyQuery(
        GET_LIVE_SESSION_INSTANCE_RECORDED_VIDEO,
        {
            client: liveClassClientService,
            fetchPolicy: "network-only",
        },
    );

    const handleRecording = (row: any) => {
        setRecordingDialog({ open: true, row, recordedUrl: "", errorMessage: "" });

        getRecordedVideo({
            variables: {
                input: {
                    liveSessionId: Number(row?.liveSessionId ?? liveSessionId),
                    liveSessionInstanceId: Number(row?.id),
                },
            },
        })
            .then(({ data: recordingData, error: recordingError }) => {
                const response = recordingData?.getLiveSessionInstanceRecordedVideo;

                if (response?.status === "success" && response?.recordedUrl) {
                    setRecordingDialog((prev) => ({
                        ...prev,
                        recordedUrl: response.recordedUrl,
                        errorMessage: "",
                    }));
                } else {
                    setRecordingDialog((prev) => ({
                        ...prev,
                        recordedUrl: "",
                        errorMessage:
                            response?.message ||
                            recordingError?.message ||
                            "Recording not available for this session instance",
                    }));
                }
            })
            .catch((err: any) => {
                setRecordingDialog((prev) => ({
                    ...prev,
                    recordedUrl: "",
                    errorMessage: err?.message || "Failed to fetch recording",
                }));
            });
    };

    const handleCloseRecording = () => {
        setRecordingDialog({ open: false, row: null, recordedUrl: "", errorMessage: "" });
    };

    useEffect(() => {
        if (!liveSessionId) return;
        getSessionInstancesList({
            variables: {
                input: {
                    limit: limit,
                    offset: (page - 1) * limit,
                    liveSessionId: Number(liveSessionId),
                    ...(externalLiveSessionsInstanceId
                        ? { externalLiveSessionsInstanceId }
                        : {}),
                },
            },
        });
    }, [page, limit, liveSessionId, externalLiveSessionsInstanceId]);

    const sessionInstancesData =
        data?.getLiveSessionInstances?.liveSessionInstances?.map((item: any) => ({
            ...item,

            startedAt: utcToIst(item.startedAt),
            endedAt: utcToIst(item.endedAt),
            createdAt: utcToIst(item.createdAt),
            updatedAt: utcToIst(item.updatedAt),
        })) || [];

    return (
        <>
            <TypographyHeading sx={{ mb: 2, mt: 3, fontSize: "25px" }}>
                Session Instances{" "}
            </TypographyHeading>

            <Paper elevation={2} sx={{ p: 2, mt: 3 }}>

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <FormControl size="small" sx={{ width: 140 }}>
                        <InputLabel>Show Entries</InputLabel>
                        <Select
                            value={limit}
                            label="Show Entries"
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1); // reset to first page
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={500}>500</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <div>
                    {loading && <Loader />}
                    {!loading && data?.getLiveSessionInstances?.liveSessionInstances?.length > 0 ? (
                        <CollapsibleTable
                            data={sessionInstancesData || []}
                            table={table}
                            id="id"
                            name="id"
                            rowsPerPageCount={limit}
                            style="false"
                            totalRecords={data?.getLiveSessionInstances?.totalCount || 0}
                            paginationSegmentApi="false"
                            viewSecondaryData="true"
                            showPaginationFooter={false}
                            handleRecording={handleRecording}
                        />
                    ) : (
                        <div>{!loading && <NoDataFound message="No Data Found" />}</div>
                    )}
                </div>

                <Box sx={{ marginTop: "20px" }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Pagination
                            count={Math.ceil((data?.getLiveSessionInstances?.totalCount || 0) / limit) || 1}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            size="medium"
                        />
                    </Stack>
                </Box>

            </Paper>

            {/* ---- Play Recording Dialog ---- */}
            <Dialog
                open={recordingDialog.open}
                onClose={handleCloseRecording}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
                    Play Recording
                    <IconButton
                        onClick={handleCloseRecording}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Session Instance ID:{" "}
                        {recordingDialog.row?.externalLiveSessionsInstanceId || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Started At: {recordingDialog.row?.startedAt || "-"} &nbsp;|&nbsp; Ended At:{" "}
                        {recordingDialog.row?.endedAt || "-"}
                    </Typography>

                    {/* fixed 16:9 box so the dialog keeps one size while the signed URL loads */}
                    <Box
                        sx={{
                            mt: 2,
                            position: "relative",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            backgroundColor: "#000",
                            borderRadius: 1,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {recordingLoading && <CircularProgress sx={{ color: "#fff" }} />}

                        {!recordingLoading && recordingDialog.recordedUrl && (
                            <video
                                key={recordingDialog.recordedUrl}
                                src={recordingDialog.recordedUrl}
                                controls
                                controlsList="nodownload"
                                style={{ width: "100%", height: "100%" }}
                            />
                        )}

                        {!recordingLoading && !recordingDialog.recordedUrl && (
                            <Typography variant="body2" sx={{ color: "#fff", px: 2, textAlign: "center" }}>
                                {recordingDialog.errorMessage || "Recording not available"}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <MuiButton variant="outlined" onClick={handleCloseRecording}>
                        Close
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    );
}